import { defineAbility, PureAbility } from '@casl/ability';
import { TAuthFeature } from '@th-common/enums/auth-feature.enum';
import { TAuthMethod } from '@th-common/enums/auth-method.enum';
import { TLicensedFeature } from '@th-common/enums/licensed-feature.enum';
import { TPermissionAbilityAction, TPermissionAbilitySubject } from '@th-common/enums/permission-ability.enum';
import { MongoQuery } from '@ucast/mongo';

type Ability = [TPermissionAbilityAction, TPermissionAbilitySubject];
export type AppAbility = PureAbility<Ability, MongoQuery>;

export function defineAbilityFor(features: TAuthFeature[], licensedFeatures: TLicensedFeature[], authMethod: TAuthMethod): AppAbility {
  return defineAbility<AppAbility>((can) => {
    can(TPermissionAbilityAction.View, TPermissionAbilitySubject.ByDefault);
    can(TPermissionAbilityAction.View, TPermissionAbilitySubject.DashboardPage);
    can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.PushConfiguration);

    if (authMethod === TAuthMethod.Password && features.includes(TAuthFeature.ChangePassword)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.ChangePasswordPage);
    }

    if (features.includes(TAuthFeature.ViewFleetStatus)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.FleetStatusPage);
    }

    if (features.includes(TAuthFeature.ViewJobs)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.Jobs);

      if (features.includes(TAuthFeature.AddEditJobs)) {
        can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.Jobs);
      }

      if (features.includes(TAuthFeature.DeleteJobs)) {
        can(TPermissionAbilityAction.Delete, TPermissionAbilitySubject.Jobs);
      }
    }

    if (features.includes(TAuthFeature.ViewVideoQueries)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.VideoQueryJob);

      if (features.includes(TAuthFeature.RequestVideoQueries)) {
        can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.VideoQueryJob);
      }

      if (features.includes(TAuthFeature.DeleteVideoQueries)) {
        can(TPermissionAbilityAction.Delete, TPermissionAbilitySubject.VideoQueryJob);
      }
    }

    if (licensedFeatures.includes(TLicensedFeature.GEO_QUERY)) {
      if (features.includes(TAuthFeature.GeographicalQueries)) {
        can(TPermissionAbilityAction.View, TPermissionAbilitySubject.GeographicalQueryJob);

        if (features.includes(TAuthFeature.RequestVideoQueries)) {
          can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.GeographicalQueryJob);
        }

        if (features.includes(TAuthFeature.DeleteVideoQueries)) {
          can(TPermissionAbilityAction.Delete, TPermissionAbilitySubject.GeographicalQueryJob);
        }
      }
    }

    if (licensedFeatures.includes(TLicensedFeature.RIDERSHIP_VALIDATION)) {
      if (features.includes(TAuthFeature.RidershipValidation)) {
        can(TPermissionAbilityAction.View, TPermissionAbilitySubject.RidershipValidationJob);

        if (features.includes(TAuthFeature.RequestVideoQueries)) {
          can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.RidershipValidationJob);
        }

        if (features.includes(TAuthFeature.DeleteVideoQueries)) {
          can(TPermissionAbilityAction.Delete, TPermissionAbilitySubject.RidershipValidationJob);
        }
      }
    }

    // Video Playback
    if (
      features.includes(TAuthFeature.VideoQueries)
      || features.includes(TAuthFeature.ViewVideoQueries)
      || features.includes(TAuthFeature.ViewStoredImagesOverCellular)
      || features.includes(TAuthFeature.ViewLiveImagesOverCellular)
    ) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.VideoPlayback);
    }

    if (
      features.includes(TAuthFeature.VideoQueries)
      || features.includes(TAuthFeature.ViewVideoQueries)
    ) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.VideoArchive);
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.DeviceOfflineConnection);
    }

    if (features.includes(TAuthFeature.ViewLiveImagesOverCellular)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.DeviceLiveConnection);
    }

    if (features.includes(TAuthFeature.ViewStoredImagesOverCellular)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.DeviceOnlineConnection);
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.VideoFileReview);
    }

    if (features.includes(TAuthFeature.DeleteVideoQueries)) {
      can(TPermissionAbilityAction.Delete, TPermissionAbilitySubject.VideoArchive);
    }

    if (features.includes(TAuthFeature.VideoStorageManagement)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.VideoStorageManagement);
    }

    // Video Player
    if (
      features.includes(TAuthFeature.ExportStoredImages)
      || features.includes(TAuthFeature.PrintStoredImages)
    ) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.CameraSnapshot);
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.GPSSnapshot);
    }

    if (features.includes(TAuthFeature.ExportStoredImages)) {
      can(TPermissionAbilityAction.Save, TPermissionAbilitySubject.CameraSnapshot);
      can(TPermissionAbilityAction.Save, TPermissionAbilitySubject.GPSSnapshot);
    }

    if (features.includes(TAuthFeature.PrintStoredImages)) {
      can(TPermissionAbilityAction.Print, TPermissionAbilitySubject.CameraSnapshot);
      can(TPermissionAbilityAction.Print, TPermissionAbilitySubject.GPSSnapshot);
    }

    if (features.includes(TAuthFeature.ManageImageBlurring)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.CameraBlurring);
    }

    if (features.includes(TAuthFeature.ExportVideoClips)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.ExportVideoClip);
    }

    //--

    if (features.includes(TAuthFeature.ServiceViewStatus)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.StatusUpdateJob);
    }

    if (features.includes(TAuthFeature.ServiceViewLog)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.LogbookUpdateJob);
    }

    if (features.includes(TAuthFeature.ViewStoredImages)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.EventDownloadJob);
    }

    if (features.includes(TAuthFeature.ServiceSyncTime)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.TimeSyncJob);
    }

    if (features.includes(TAuthFeature.ApplyConfig)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.SyncConfigurationJob);
    }

    if (features.includes(TAuthFeature.ServiceUpdateFirmware)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.FirmwareUpdateJob);
    }

    if (features.includes(TAuthFeature.AcceptRejectVerificationImage)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.CameraVerificationJob);
    }

    if (features.includes(TAuthFeature.ViewFleetDatabase)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.Database);
    }

    if (features.includes(TAuthFeature.ViewService)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.Service);
    }

    if (features.includes(TAuthFeature.EditUsers)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.UserGroups);
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.UserManagement);
      can(TPermissionAbilityAction.Set, TPermissionAbilitySubject.GroupManagement);
    }

    if (features.includes(TAuthFeature.EditRoles)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.UserGroups);
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.GroupManagement);
      can(TPermissionAbilityAction.Set, TPermissionAbilitySubject.GroupManagement);
    }

    if (features.includes(TAuthFeature.SetReferenceImage)) {
      can(TPermissionAbilityAction.Set, TPermissionAbilitySubject.ReferenceImage);
    }

    if (features.includes(TAuthFeature.AcceptRejectVerificationImage)) {
      can(TPermissionAbilityAction.AcceptReject, TPermissionAbilitySubject.VerificationImage);
    }

    if (licensedFeatures.includes(TLicensedFeature.CELLULAR)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.NetworkScope);
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.AddDeviceCellular);
    }

    if (
      licensedFeatures.includes(TLicensedFeature.CELLULAR)
      && features.includes(TAuthFeature.OverrideJobNetworkScope)
    ) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.NetworkScope);
    }

    if (licensedFeatures.includes(TLicensedFeature.CELLULAR) && features.includes(TAuthFeature.ChangeDownloadJobNetworkScope)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.DownloadNetworkScope);
    }

    if (features.includes(TAuthFeature.AddVehicleToFleet)) {
      can(TPermissionAbilityAction.Manage, TPermissionAbilitySubject.Database);
    }

    if (features.includes(TAuthFeature.RemoveVehicleFromFleet)) {
      can(TPermissionAbilityAction.Delete, TPermissionAbilitySubject.Database);
    }

    if (features.includes(TAuthFeature.ShowDebugInfo)) {
      can(TPermissionAbilityAction.View, TPermissionAbilitySubject.ShowDebugInfo);
    }
  });
}
